<template>
    <div class="acias-generating relative overflow-hidden rounded-[24px] border border-cyan-200 bg-[radial-gradient(circle_at_top_left,_rgba(236,254,255,0.95),_rgba(248,250,252,0.98)_55%,_rgba(255,255,255,1))] p-5 shadow-sm">
        <div class="flex items-start gap-4">
            <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] bg-white shadow-sm ring-1 ring-cyan-100">
                <div class="relative h-7 w-7">
                    <div class="absolute inset-0 rounded-full border-4 border-cyan-100"></div>
                    <div class="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-cyan-500 border-r-cyan-300"></div>
                </div>
            </div>

            <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                    <div class="text-sm font-semibold text-slate-800">Gerando ACIA</div>
                    <span class="rounded-full bg-cyan-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-700">
                        Em andamento
                    </span>
                </div>
                <p class="mt-1 text-xs text-slate-500">
                    Cruzando {{ filesCount || 'os' }} documento(s) com a avaliação · começou {{ elapsedLabel }}
                </p>
            </div>
        </div>

        <!-- Etapas do protocolo -->
        <div class="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div
                v-for="(step, index) in steps"
                :key="step.label"
                class="rounded-2xl border px-3 py-2 transition-all duration-700"
                :class="index <= activeStep ? 'border-cyan-300 bg-cyan-50/80' : 'border-slate-100 bg-slate-50/60'"
            >
                <div class="flex items-center gap-1.5">
                    <span
                        class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold transition-all duration-700"
                        :class="index < activeStep
                            ? 'bg-cyan-500 text-white'
                            : index === activeStep
                                ? 'bg-cyan-100 text-cyan-600 ring-1 ring-cyan-400 animate-pulse'
                                : 'bg-slate-200 text-slate-400'"
                    >
                        <svg v-if="index < activeStep" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-3 w-3">
                            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                        </svg>
                        <template v-else>{{ index + 1 }}</template>
                    </span>
                    <span
                        class="text-[10px] font-semibold uppercase tracking-[0.1em]"
                        :class="index <= activeStep ? 'text-cyan-700' : 'text-slate-400'"
                    >
                        {{ step.label }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Console de frases digitadas -->
        <div class="mt-4 h-[68px] overflow-hidden rounded-[18px] border border-slate-100 bg-slate-950 px-4 py-3 text-xs text-slate-300 shadow-inner">
            <div class="font-mono leading-6">
                <span class="text-cyan-500">&gt;</span> {{ typedPhrase }}
                <span class="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-cyan-300 align-middle"></span>
            </div>
        </div>

        <!-- Barra de progresso -->
        <div class="mt-4">
            <div class="h-1.5 overflow-hidden rounded-full bg-slate-200">
                <div class="acias-progress h-full rounded-full bg-gradient-to-r from-cyan-600 via-cyan-400 to-cyan-600"></div>
            </div>
            <p class="mt-3 text-[11px] leading-5 text-slate-400">
                Pode levar alguns minutos. Você pode continuar usando o sistema — o resultado aparece aqui sozinho.
            </p>
        </div>

        <div class="acias-shine"></div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const PHRASES = [
    'Inventariando documentos anexados e classificando exames laboratoriais, imagem e testes de força...',
    'Extraindo valores objetivos de cada documento — laudos, medidas, marcadores e datas...',
    'Carregando avaliação funcional: saltos, força, mobilidade, Y-Balance e core...',
    'Sobrepondo matriz de risco por segmento corporal aos achados dos exames...',
    'Cruzando marcadores laboratoriais com déficits de força e padrão de recuperação...',
    'Correlacionando achados de imagem com assimetrias funcionais e queixa principal...',
    'Validando cruzamentos contra referências normativas (LSI, Y-Balance, dorsiflexão)...',
    'Hierarquizando riscos e montando o plano de intervenção priorizado...',
    'Estruturando o relatório final com síntese executiva e gráficos clínicos...'
];

const STEPS = [
    { label: 'Inventário' },
    { label: 'Extração' },
    { label: 'Cruzamento' },
    { label: 'Síntese' }
];

// Segundos decorridos a partir dos quais cada etapa acende. Derivar a etapa do tempo
// (e não de um timer local) mantém a barra coerente depois de um F5 no meio da geração.
const STEP_THRESHOLDS_SEC = [0, 20, 50, 95];

export default {
    name: 'AciasGeneratingCard',
    props: {
        filesCount: {
            type: Number,
            default: 0
        },
        // Início da geração (created_at da ACIA) — base do tempo decorrido
        startedAt: {
            type: String,
            default: ''
        }
    },
    setup(props) {
        const typedPhrase = ref('');
        const phraseIndex = ref(0);
        const elapsedSec = ref(0);

        let typingTimer = null;
        let holdTimer = null;
        let elapsedTimer = null;

        const startedMs = computed(() => {
            const parsed = new Date(props.startedAt).getTime();
            return Number.isNaN(parsed) ? Date.now() : parsed;
        });

        const activeStep = computed(() => {
            let index = 0;
            STEP_THRESHOLDS_SEC.forEach((threshold, i) => {
                if (elapsedSec.value >= threshold) index = i;
            });
            return index;
        });

        const elapsedLabel = computed(() => {
            const seconds = elapsedSec.value;
            if (seconds < 60) return 'agora há pouco';
            const minutes = Math.floor(seconds / 60);
            return `há ${minutes} min`;
        });

        function typePhrase() {
            const phrase = PHRASES[phraseIndex.value % PHRASES.length];
            typedPhrase.value = '';

            const step = (charIndex = 0) => {
                typedPhrase.value = phrase.slice(0, charIndex);

                if (charIndex <= phrase.length) {
                    typingTimer = setTimeout(() => step(charIndex + 1), 22);
                    return;
                }

                holdTimer = setTimeout(() => {
                    phraseIndex.value = (phraseIndex.value + 1) % PHRASES.length;
                    typePhrase();
                }, 1400);
            };

            step(1);
        }

        function tickElapsed() {
            elapsedSec.value = Math.max(0, Math.floor((Date.now() - startedMs.value) / 1000));
        }

        onMounted(() => {
            tickElapsed();
            elapsedTimer = setInterval(tickElapsed, 1000);
            typePhrase();
        });

        onBeforeUnmount(() => {
            [typingTimer, holdTimer].forEach((timer) => timer && clearTimeout(timer));
            if (elapsedTimer) clearInterval(elapsedTimer);
        });

        return {
            typedPhrase,
            activeStep,
            elapsedLabel,
            steps: STEPS
        };
    }
};
</script>

<style scoped>
.acias-progress {
    width: 40%;
    animation: acias-progress-slide 2.4s ease-in-out infinite;
}

@keyframes acias-progress-slide {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
}

.acias-shine {
    position: absolute;
    top: 0;
    left: 100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.12), transparent);
    animation: acias-card-shine 3.2s linear infinite;
    pointer-events: none;
}

@keyframes acias-card-shine {
    0%   { left: 100%; }
    100% { left: -45%; }
}
</style>
